import React from 'react';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import { InitialsAvatar } from '../marketing/components/icons';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const cases = [
  {
    segment: 'Portfolio landlord',
    title: '34 units, zero missed deadlines',
    body: "James ran his portfolio across spreadsheets and calendar reminders. After moving to MillOS, every certificate is tracked automatically and renewals are booked 90 days ahead.",
    name: 'James Whitfield',
    role: 'Portfolio landlord · Bristol',
    metrics: [
      { v: '0', l: 'Missed deadlines in 12 months' },
      { v: '5 hrs', l: 'Saved per month' },
      { v: '100', l: 'Compliance score' },
      { v: '34', l: 'Units on one dashboard' },
    ],
  },
  {
    segment: 'Letting agent',
    title: 'Onboarding cut from hours to minutes',
    body: "Priya's agency manages properties for 80 landlords. MillOS's AI certificate reading turned a manual onboarding process into a drag-and-drop upload.",
    name: 'Priya Ramesh',
    role: 'Letting agent · Manchester',
    metrics: [
      { v: '92%', l: 'Faster onboarding' },
      { v: '80', l: 'Landlord clients' },
      { v: '1 hr', l: 'Support response time' },
      { v: '3×', l: 'Properties per staff member' },
    ],
  },
  {
    segment: 'HMO operator',
    title: 'Three spreadsheets replaced by one view',
    body: "Daniel's HMOs meant tracking rent per room across multiple bank accounts. Open Banking now matches every payment to the right room automatically.",
    name: 'Daniel Okafor',
    role: 'HMO operator · Leeds',
    metrics: [
      { v: '100%', l: 'Rent matched automatically' },
      { v: '6 hrs', l: 'Saved per month' },
      { v: '48', l: 'Rooms tracked' },
      { v: '0', l: 'Reconciliation errors' },
    ],
  },
];

const CaseStudiesPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Case Studies — Real Landlord Results | MillOS',
    description: 'How UK landlords, agents and HMO operators use MillOS to eliminate missed deadlines, cut admin hours and pass audits with confidence.',
    canonicalPath: '/case-studies',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="Case studies"
        title={<>Real portfolios, <Box component="em" sx={{ fontStyle: 'italic' }}>real results</Box></>}
        body="How landlords and agents cut admin, eliminated missed deadlines and passed audits with MillOS."
      />
      <Box component="section" sx={{ padding: { xs: '48px 16px 64px', md: '56px 52px 96px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {cases.map((c) => (
            <Box key={c.name} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' }, gap: { xs: '24px', md: '36px' }, alignItems: 'center', background: C.panel, borderRadius: '28px', padding: { xs: '28px', md: '40px' } }}>
              <Box>
                <Box sx={{ display: 'inline-block', background: C.white, color: C.accent, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '32px', padding: '5px 14px', fontSize: 11, fontWeight: 500, letterSpacing: '0.06rem', textTransform: 'uppercase' }}>
                  {c.segment}
                </Box>
                <Box component="h2" sx={{ fontFamily: F.display, fontSize: 30, lineHeight: 1.1, marginTop: '16px' }}>{c.title}</Box>
                <Box sx={{ marginTop: '14px', color: C.body, fontSize: 15, lineHeight: 1.6 }}>{c.body}</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '22px' }}>
                  <InitialsAvatar name={c.name} size={42} />
                  <Box>
                    <Box sx={{ fontWeight: 500, fontSize: 14 }}>{c.name}</Box>
                    <Box sx={{ fontSize: 13, color: C.muted }}>{c.role}</Box>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                {c.metrics.map((m) => (
                  <Box key={m.l} sx={{ background: C.white, borderRadius: '18px', padding: '24px' }}>
                    <Box sx={{ fontFamily: F.display, fontSize: 38, lineHeight: 1, letterSpacing: '-0.02em', color: C.accent }}>{m.v}</Box>
                    <Box sx={{ marginTop: '8px', fontSize: 13, color: C.body }}>{m.l}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default CaseStudiesPage;
